/**
 * The render half of selective rendering (design doc §11.6, K2).
 *
 * Shared by `PriceTable`, `ScheduleCard` and `EventsList` — three different
 * row shapes (`<tr>`, `<tr>`, `<li>`), one selection rule
 * (`../collapsibleRows.ts`). The caller supplies the markup through
 * `renderRow`/`renderToggle`; this component only ever decides WHICH rows are
 * in `shown` and whether the toggle exists at all.
 *
 * No filtering happens here or in the selector it calls — `rows` always holds
 * every row the card was given, and `expanded` is local UI state, never a prop
 * that could feed back into what data a card holds.
 */

import { useState, type ReactNode } from 'react';
import { selectVisibleRows } from '../collapsibleRows';

export type RowState = {
  highlighted: boolean;
  /** This row was behind the disclosure and just appeared — for a one-time
      entrance animation; the rows visible from the start get none. */
  revealed: boolean;
};

export default function CollapsibleRows<T>({
  rows,
  highlight,
  keyOf,
  renderRow,
  renderToggle,
}: {
  rows: readonly T[];
  highlight?: readonly string[] | null;
  /** The row's own key in the feed, or `null`/`undefined` for an unkeyed row. */
  keyOf: (row: T) => string | null | undefined;
  renderRow: (row: T, state: RowState) => ReactNode;
  /**
   * Wraps the disclosure control in whatever the caller's markup needs — a
   * `<tr><td colSpan=…>` for a table, a plain `<li>` for a list.
   */
  renderToggle: (button: ReactNode) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const { visible, hidden } = selectVisibleRows(rows, highlight, keyOf);

  const isHighlighted = (row: T) => {
    if (!highlight || highlight.length === 0) return false;
    const key = keyOf(row);
    return !!key && highlight.includes(key);
  };

  return (
    <>
      {visible.map((row) => renderRow(row, { highlighted: isHighlighted(row), revealed: false }))}
      {expanded
        ? hidden.map((row) => renderRow(row, { highlighted: isHighlighted(row), revealed: true }))
        : null}
      {hidden.length > 0
        ? renderToggle(
            <button
              type="button"
              className="disclosure"
              // No `role="status"` here (the same lesson `ActivityFeed`'s
              // header carries, !265): an explicit role would replace the
              // implicit `button` role and strand `aria-expanded` on a
              // non-widget. Full data is always one tap away — the button IS
              // the "показано X из Y" counter, not a caption beside it.
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Свернуть' : `Показать ещё ${hidden.length} · показано ${visible.length} из ${rows.length}`}
            </button>,
          )
        : null}
    </>
  );
}
